import { clearPrivateMemberInvitationForTeam } from "./privateMemberInvitation";

const LOGIN_MODAL_WECHAT_RETURN_KEY = "loginModalWechatReturn";
const LOGIN_MODAL_WECHAT_RETURN_COOKIE = "loginModalWechatReturn";

// Keep the handoff available when the OAuth callback lands on another
// application subdomain. These are the same parent domains used by auth
// cookies in util/utils.js.
const SHARED_COOKIE_DOMAINS = [
  ".ngtest.guyubao.com",
  ".ngrok.xiaomiqiu123.top",
  ".xiaomiqiu123.top",
  ".panzitech.com",
  ".bujue.art",
];

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const getSharedCookieDomain = (hostname = window.location.hostname) => {
  const normalizedHostname = String(hostname || "").toLowerCase();
  return (
    SHARED_COOKIE_DOMAINS.find(
      (domain) =>
        normalizedHostname === domain.slice(1) ||
        normalizedHostname.endsWith(domain)
    ) || ""
  );
};

const getCookieValues = (name) => {
  if (typeof document === "undefined") return [];

  const prefix = `${name}=`;
  return document.cookie
    .split(";")
    .map((item) => item.trim())
    .filter((item) => item.startsWith(prefix))
    .map((item) => item.slice(prefix.length));
};

const setReturnCookie = (value) => {
  if (typeof document === "undefined") return false;

  const encodedValue = encodeURIComponent(value);
  // Browsers commonly cap one cookie at about 4 KB. Falling back to
  // sessionStorage is preferable to writing a cookie that will be truncated.
  if (encodedValue.length > 3800) return false;

  const attributes = ["path=/", "SameSite=Lax"];
  const domain = getSharedCookieDomain();
  if (domain) attributes.push(`domain=${domain}`);
  if (window.location.protocol === "https:") attributes.push("Secure");

  // Remove host-only values from older builds so a stale duplicate cannot win
  // when document.cookie is read on the source subdomain.
  clearReturnCookie();
  document.cookie = `${LOGIN_MODAL_WECHAT_RETURN_COOKIE}=${encodedValue};${attributes.join(
    ";"
  )}`;
  return getCookieValues(LOGIN_MODAL_WECHAT_RETURN_COOKIE).includes(encodedValue);
};

const clearReturnCookie = () => {
  if (typeof document === "undefined") return;

  const attributes = ["path=/", "expires=Thu, 01 Jan 1970 00:00:00 GMT"];
  const domain = getSharedCookieDomain();
  const domainAttributes = domain ? [`domain=${domain}`] : [];
  document.cookie = `${LOGIN_MODAL_WECHAT_RETURN_COOKIE}=;${attributes.concat(
    domainAttributes
  ).join(";")}`;
  // Also remove a host-only cookie created by an older build or an unknown
  // local domain.
  document.cookie = `${LOGIN_MODAL_WECHAT_RETURN_COOKIE}=;${attributes.join(
    ";"
  )}`;
};

const isTrustedReturnOrigin = (origin) => {
  if (typeof window === "undefined" || typeof origin !== "string") {
    return false;
  }

  try {
    const target = new URL(origin);
    const current = new URL(window.location.origin);
    if (
      target.protocol !== current.protocol ||
      target.port !== current.port ||
      target.username ||
      target.password ||
      target.pathname !== "/" ||
      target.search ||
      target.hash
    ) {
      return false;
    }

    if (target.origin === current.origin) return true;
    const currentDomain = getSharedCookieDomain(current.hostname);
    const targetDomain = getSharedCookieDomain(target.hostname);
    return Boolean(currentDomain && currentDomain === targetDomain);
  } catch (error) {
    return false;
  }
};

const normalizeReturnTarget = (value) => {
  if (typeof window === "undefined" || typeof value !== "string") {
    return null;
  }
  if (value.includes("\\")) return null;

  try {
    const isRelative = value.startsWith("/") && !value.startsWith("//");
    const isAbsolute = /^https?:\/\//i.test(value);
    if (!isRelative && !isAbsolute) return null;
    const url = new URL(value, window.location.origin);
    if (!isRelative && !isTrustedReturnOrigin(url.origin)) return null;
    return {
      returnUrl: `${url.pathname}${url.search}${url.hash}`,
      returnOrigin: url.origin,
    };
  } catch (error) {
    return null;
  }
};

export const normalizeLoginModalReturnUrl = (value) => {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//")
  ) {
    return "";
  }
  return normalizeReturnTarget(value)?.returnUrl || "";
};

const normalizePositiveInteger = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const normalizeLoginModalWechatReturnContext = (state) => {
  if (!state || typeof state !== "object") return null;

  const returnUrl = normalizeLoginModalReturnUrl(state.returnUrl);
  const returnOrigin = state.returnOrigin || window.location.origin;
  if (!returnUrl || !isTrustedReturnOrigin(returnOrigin)) return null;

  const teamId = String(state.teamId || "").trim();
  const shouldJoin = state.shouldJoin === true && Boolean(teamId);
  const source = typeof state.source === "string" ? state.source : "";
  const inviterId = normalizePositiveInteger(state.inviterId);

  return {
    returnUrl:
      returnOrigin === window.location.origin
        ? returnUrl
        : `${returnOrigin}${returnUrl}`,
    returnOrigin,
    shouldJoin,
    teamId: shouldJoin ? teamId : "",
    source: shouldJoin ? source : "",
    inviterId: shouldJoin ? inviterId : null,
  };
};

export const saveLoginModalWechatReturnContext = ({
  returnUrl,
  shouldJoin = false,
  teamId = "",
  source = "",
  inviterId = null,
} = {}) => {
  const storage = getSessionStorage();
  const target = normalizeReturnTarget(
    typeof returnUrl === "string" && returnUrl
      ? returnUrl
      : `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
  if (!target) return null;

  const normalizedTeamId = String(teamId || "").trim();
  const normalizedShouldJoin = shouldJoin === true && Boolean(normalizedTeamId);
  const context = {
    returnUrl: target.returnUrl,
    returnOrigin: target.returnOrigin,
    shouldJoin: normalizedShouldJoin,
    teamId: normalizedShouldJoin ? normalizedTeamId : "",
    source:
      normalizedShouldJoin && typeof source === "string" ? source : "",
    inviterId: normalizedShouldJoin
      ? normalizePositiveInteger(inviterId)
      : null,
  };

  const serializedContext = JSON.stringify(context);
  let saved = false;
  try {
    storage?.setItem(LOGIN_MODAL_WECHAT_RETURN_KEY, serializedContext);
    saved = Boolean(storage);
  } catch (error) {
    // Cookie storage below can still bridge the callback if sessionStorage is
    // unavailable or disabled.
  }
  saved = setReturnCookie(serializedContext) || saved;
  return saved ? context : null;
};

export const saveLoginModalWechatReturnUrl = (context) =>
  saveLoginModalWechatReturnContext(context)?.returnUrl || "";

export const clearLoginModalWechatReturnUrl = () => {
  try {
    getSessionStorage()?.removeItem(LOGIN_MODAL_WECHAT_RETURN_KEY);
  } catch (error) {
    // Storage can be unavailable in browsers that block site data.
  }
  clearReturnCookie();
};

const readLoginModalWechatReturnContext = (consume) => {
  const storage = getSessionStorage();
  const rawStates = [];
  try {
    getCookieValues(LOGIN_MODAL_WECHAT_RETURN_COOKIE).forEach((cookieState) => {
      if (cookieState) rawStates.push(decodeURIComponent(cookieState));
    });
  } catch (error) {
    // Ignore malformed cookie values and try the page-local state below.
  }
  try {
    const sessionState = storage?.getItem(LOGIN_MODAL_WECHAT_RETURN_KEY);
    if (sessionState) rawStates.push(sessionState);
  } catch (error) {
    // Storage can be unavailable in browsers that block site data.
  }

  for (const rawState of rawStates) {
    try {
      const context = normalizeLoginModalWechatReturnContext(
        JSON.parse(rawState)
      );
      if (!context) continue;
      if (consume) clearLoginModalWechatReturnUrl();
      return context;
    } catch (error) {
      // Try the next storage source; an invalid cookie must not hide a valid
      // sessionStorage value.
    }
  }

  if (rawStates.length) clearLoginModalWechatReturnUrl();
  return null;
};

export const getLoginModalWechatReturnContext = () =>
  readLoginModalWechatReturnContext(false);

export const consumeLoginModalWechatReturnContext = () => {
  const context = readLoginModalWechatReturnContext(true);
  clearPrivateMemberInvitationForTeam(context?.teamId);
  return context;
};

export const getLoginModalWechatReturnUrl = () =>
  getLoginModalWechatReturnContext()?.returnUrl || "";

export const consumeLoginModalWechatReturnUrl = () =>
  consumeLoginModalWechatReturnContext()?.returnUrl || "";
